import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Phone, MapPin, Clock, Send, Facebook, Instagram, Heart } from "lucide-react";
import { CONTACT } from "@/constants/config";
import PageMeta from "@/components/PageMeta";

const ADDRESS_LINES = [CONTACT.address];
const PHONE_LINES = [CONTACT.phone];
const EMAIL_LINES = [CONTACT.email];

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({ full_name: user?.name || "", email: user?.email || "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch {
      alert("Something went wrong. Please email us directly.");
    }
  };

  return (
    <div>
      <PageMeta
        title="Contact — Adoption & Foster Inquiries | Lavish 808 Bulliez Rescued Paws"
        description="Contact Lavish 808 Bulliez Rescued Paws for bulldog adoption, fostering, volunteering or partnerships. Based in Murrieta, California — call 808-450-4960 or send a message."
      />
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Get in touch</div>
          <h1 className="font-serif text-5xl md:text-7xl mb-6" style={{ color: "#0E5C6E" }}>E komo mai — say aloha.</h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            Questions about adoption, fostering, or partnerships? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-6">
            <ContactLine icon={<MapPin />} title="Visit" lines={ADDRESS_LINES} />
            <ContactLine icon={<Phone />} title="Call" lines={PHONE_LINES} href={`tel:${CONTACT.phone}`} />
            <ContactLine icon={<Mail />} title="Email" lines={EMAIL_LINES} href={`mailto:${CONTACT.email}`} />
            <div className="p-6 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
              <div className="flex items-center gap-3 mb-4">
                <Clock size={20} color="#0E5C6E" />
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#0E5C6E" }}>Hours</div>
              </div>
              <ul className="space-y-2" style={{ color: "#2D2B2A" }}>
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between text-sm">
                    <span>{h.day}</span><span className="font-medium">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-3">
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-11 h-11 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: "#0E5C6E", color: "white" }} data-testid="contact-facebook">
                <Facebook size={18} aria-hidden="true" />
              </a>
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-11 h-11 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: "#E27E4E", color: "white" }} data-testid="contact-instagram">
                <Instagram size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <div className="p-12 rounded-3xl text-center" style={{ backgroundColor: "#E4EFE9" }} data-testid="contact-success">
                <Heart size={36} color="#0E5C6E" fill="#0E5C6E" className="mx-auto mb-4" />
                <div className="font-serif text-4xl mb-2" style={{ color: "#0E5C6E" }}>Mahalo!</div>
                <div style={{ color: "#2D2B2A" }}>Your message is in our inbox. We'll reply within 48 hours.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form" aria-label="Contact form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name-input" className="sr-only">Your name</label>
                    <input id="contact-name-input" required placeholder="Your name" aria-label="Your name" className="form-input w-full" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="contact-name" />
                  </div>
                  <div>
                    <label htmlFor="contact-email-input" className="sr-only">Email address</label>
                    <input id="contact-email-input" required type="email" placeholder="Email" aria-label="Email address" className="form-input w-full" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                  </div>
                </div>
                <label htmlFor="contact-subject-input" className="sr-only">Subject</label>
                <input id="contact-subject-input" required placeholder="Subject" aria-label="Subject" className="form-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
                <label htmlFor="contact-message-input" className="sr-only">Your message</label>
                <textarea id="contact-message-input" rows="6" required placeholder="How can we help?" aria-label="Your message" className="form-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                <button type="submit" className="btn-primary" data-testid="contact-submit">
                  <Send size={16} aria-hidden="true" /> Send message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactLine({ icon, title, lines, href }) {
  const content = (
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#F7ECD9", color: "#0E5C6E" }}>
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: "#E27E4E" }}>{title}</div>
        {lines.map((l) => <div key={l} className="text-sm" style={{ color: "#2D2B2A" }}>{l}</div>)}
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{content}</a> : content;
}
